/* eslint-disable no-restricted-globals */

import { ShoppingListProduct } from "~/bff/types/ShoppingListProduct";
import { EventsNames } from "~/constants/data-layer";
import { getDisplayPriceWithoutCurrency } from "~/helpers/get-display-price";

import { CURRENCY_CODES } from "../../constants/i18n";

export interface TriggerDYEventProductParams {
  sku: string | null;
  currency: CURRENCY_CODES;
  price: number;
  size: string | null;
  quantity: number;
  existingProducts?: (ShoppingListProduct | null)[];
}

export interface TriggerDYEventUserParams {
  type: DYLoginSignUpTypes.loginV1 | DYLoginSignUpTypes.signupV1;
  userId: string;
}

export interface TriggerCheckAvailabilityRateParams {
  sku: string;
  sizeId: string;
  sizeName?: string;
}

export enum DYEventNames {
  DYLogin = "Login",
  DYSignup = "Signup",
  DYCheckAvailability = "check-availability-rate",
}

export enum DYLoginSignUpTypes {
  signupV1 = "signup-v1",
  loginV1 = "login-v1",
}

export interface DYEventObject {
  name: EventsNames;
  properties: {
    dyType: string;
    productId: string;
    value: number;
    currency: CURRENCY_CODES;
    quantity: number;
    size: string;
    cart: Cart[];
  };
}

export interface DYLoginAndSignUpEventObject {
  name: DYEventNames;
  properties: {
    dyType: DYLoginSignUpTypes.loginV1 | DYLoginSignUpTypes.signupV1;
    cuid: string;
    cuidType: string;
  };
}

export interface DYCheckAvailabilityRateEventObject {
  name: DYEventNames.DYCheckAvailability;
  properties: {
    sku: string;
    sizeId: string;
    sizeName: string;
  };
}

export interface Cart {
  productId?: string;
  quantity?: number;
  itemPrice: number;
  size?: string;
}

export const triggerAddToCartDYEvent = (
  productData: TriggerDYEventProductParams,
): void => {
  if (window.DY && typeof window.DY?.API === "function") {
    window.DY.API("event", createDYAddToCartEventObject(productData));
  }
};

export const triggerLoginAndSignUpDYEvent = (
  userData: TriggerDYEventUserParams,
): void => {
  if (window.DY && typeof window.DY?.API === "function") {
    window.DY.API("event", createDYLoginAndSignUpEventObject(userData));
  }
};

export const triggerCheckAvailabilityRateEvent = (
  userData: TriggerCheckAvailabilityRateParams,
): void => {
  if (window.DY && typeof window.DY?.API === "function") {
    window.DY.API("event", createDYCheckAvailabilityRateObject(userData));
  }
};

export const createDYAddToCartEventObject = (
  productData: TriggerDYEventProductParams,
): DYEventObject | null => {
  if (!productData?.sku || !productData?.size) {
    return null;
  }

  return {
    name: EventsNames.addToCart,
    properties: {
      dyType: "add-to-cart-v1",
      productId: productData?.sku,
      value: productData?.price * productData?.quantity,
      currency: productData?.currency,
      quantity: productData?.quantity,
      size: productData?.size,
      cart: createCart(productData),
    },
  };
};

export const createDYLoginAndSignUpEventObject = (
  userData: TriggerDYEventUserParams,
): DYLoginAndSignUpEventObject => {
  return {
    name:
      userData?.type === DYLoginSignUpTypes.loginV1
        ? DYEventNames.DYLogin
        : DYEventNames.DYSignup,
    properties: {
      dyType: userData?.type,
      cuid: userData.userId,
      cuidType: "userUid",
    },
  };
};

export const createDYCheckAvailabilityRateObject = (
  userData: TriggerCheckAvailabilityRateParams,
): DYCheckAvailabilityRateEventObject => {
  return {
    name: DYEventNames.DYCheckAvailability,
    properties: {
      sku: userData.sku,
      sizeId: userData.sizeId,
      sizeName: userData.sizeName ?? "",
    },
  };
};

export const createCart = (productData: TriggerDYEventProductParams): Cart[] => {
  const cart = productData?.existingProducts
    ? productData.existingProducts.map((product) => {
        return {
          productId: product?.sku ?? undefined,
          quantity: product?.quantity ?? undefined,
          itemPrice: Number(getDisplayPriceWithoutCurrency(product?.price)),
          size: product?.displaySize ?? undefined,
        };
      })
    : [];

  const existingProductIndex = cart.findIndex(
    (product) => product?.productId === productData?.sku,
  );

  if (existingProductIndex >= 0) {
    cart[existingProductIndex].quantity =
      (cart?.[existingProductIndex]?.quantity ?? 0) + productData?.quantity;
  } else {
    cart.push({
      productId: productData?.sku ?? undefined,
      quantity: productData?.quantity ?? undefined,
      itemPrice: productData?.price,
      size: productData?.size ?? undefined,
    });
  }

  return cart;
};

/* eslint-enable no-restricted-globals */
